import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import logo from '../../../assets/img/profile/logo.png';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginUser, logoutUser } from '../../../redux/slices/authSlice';
import {
  MdApartment,
  MdEmail,
  MdLock,
  MdOutlineRemoveRedEye,
  MdQueryStats,
  MdShield,
  MdVerifiedUser,
} from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { RootState } from '../../../redux/store';
import { motion } from 'framer-motion';
import axios from 'axios';
import TwoFactorPrompt from '../../TwoView/TwoFactorPrompt';
import { getCookie } from 'cookies-next';
import ReCAPTCHA from 'react-google-recaptcha';

const MotionBox = motion(Box);
const RECAPTCHA_SITEKEY = '6LfIAa0rAAAAAOLNaR0Vodz07Wst3sN_vGEXQXSQ';

function SignIn() {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [mantenerSesion, setMantenerSesion] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const user = useSelector((state: RootState) => state.auth.user);
  const { handleSubmit, register } = useForm();

  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [require2FA, setRequire2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');

  useEffect(() => {
    const token = getCookie('sesion_t');
    if (!token && user) dispatch(logoutUser());
    if (user && token) navigate('/admin/index');
  }, [user, navigate, dispatch]);

  const onSubmit = async (values: any) => {
    if (!recaptchaToken) {
      toast({
        title: 'Completa el reCAPTCHA',
        description: 'Verifica el reCAPTCHA para continuar.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      try { recaptchaRef.current?.execute?.(); } catch {}
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000'}/login`,
        {
          email: values.email,
          password: values.password,
          captchaToken: recaptchaToken,
          captchaProvider: 'recaptcha',
        }
      );

      const data = res.data;

      if (data.require2FA) {
        setTempToken(data.tempToken);
        setRequire2FA(true);
        return;
      }

      dispatch(loginUser({
        id: data.user.id,
        name: data.user.name,
        email: data.user.email,
        rol: data.user.rol,
      }));

      const maxAge = mantenerSesion ? 60 * 60 * 24 * 30 : 60 * 60 * 24 * 7;
      document.cookie = `sesion_t=${data.accessToken}; path=/; max-age=${maxAge}; SameSite=Lax`;

      toast({
        title: `Bienvenido, ${data.user.name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/admin/index');
    } catch (err: any) {
      toast({
        title: 'Acceso denegado',
        description: err?.response?.data?.message || 'Correo o contrasena incorrectos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      try { recaptchaRef.current?.reset(); setRecaptchaToken(null); } catch {}
    } finally {
      setIsLoading(false);
    }
  };

  if (require2FA) {
    return <TwoFactorPrompt tempToken={tempToken} onSuccess={() => navigate('/admin/index')} />;
  }

  const highlights = [
    { icon: MdApartment, label: 'Gestion municipal', text: 'Tramites, padrones y expedientes en un solo entorno.' },
    { icon: MdVerifiedUser, label: 'Control seguro', text: 'Acceso con captcha, sesion protegida y segundo factor.' },
    { icon: MdQueryStats, label: 'Operacion visible', text: 'Indicadores y seguimiento para areas administrativas.' },
  ];

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      position="relative"
      overflow="hidden"
      bg="#f7f4ee"
      px={{ base: 4, md: 8 }}
      py={{ base: 8, lg: 12 }}
    >
      <Box position="absolute" inset={0} bgGradient="linear(160deg, #f7f4ee, #efe6db)" />

      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        maxW="1180px"
        w="100%"
        minH={{ lg: '690px' }}
        borderRadius={{ base: '30px', lg: '36px' }}
        overflow="hidden"
        zIndex={1}
        boxShadow="0 34px 90px rgba(64, 29, 24, 0.18)"
        border="1px solid rgba(104,26,45,0.08)"
        bg="white"
      >
        <Box
          position="relative"
          p={{ base: 7, md: 10, xl: 12 }}
          color="white"
          bgGradient="linear(145deg, #2b0913 0%, #681a2d 52%, #5b182b 100%)"
          overflow="hidden"
        >
          <Box position="absolute" top="-80px" right="-100px" w="280px" h="280px" borderRadius="full" bg="rgba(187,140,82,0.12)" />

          <VStack align="stretch" spacing={{ base: 8, lg: 12 }} position="relative" h="100%">
            <HStack spacing={4}>
              <Flex
                w="76px"
                h="76px"
                align="center"
                justify="center"
                borderRadius="24px"
                bg="white"
                boxShadow="0 18px 45px rgba(0,0,0,0.28)"
              >
                <Image src={logo} alt="Escudo Totolac" maxW="62px" maxH="62px" objectFit="contain" />
              </Flex>
              <Box>
                <Text fontSize="xs" letterSpacing="0.18em" textTransform="uppercase" color="rgba(255,255,255,0.72)">
                  Ayuntamiento de Totolac
                </Text>
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" lineHeight="1.05">
                  Plataforma Administrativa
                </Text>
              </Box>
            </HStack>

            <Box maxW="520px">
              <Badge
                px={4}
                py={2}
                borderRadius="full"
                bg="rgba(187,140,82,0.18)"
                color="#f6d7aa"
                border="1px solid rgba(246,215,170,0.32)"
                letterSpacing="0.12em"
              >
                Gestion 2025 - 2028
              </Badge>
              <Text mt={6} fontSize={{ base: '34px', md: '48px' }} fontWeight="800" lineHeight="0.96" letterSpacing="-1.8px">
                Control operativo con identidad institucional.
              </Text>
              <Text mt={5} color="rgba(255,255,255,0.78)" fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.7">
                Acceso centralizado para Obras Publicas, Tesoreria, Catastro, padrones y seguimiento documental del municipio.
              </Text>
            </Box>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              {highlights.map((item) => (
                <Box
                  key={item.label}
                  p={4}
                  borderRadius="22px"
                  bg="rgba(255,255,255,0.10)"
                  border="1px solid rgba(255,255,255,0.14)"
                  backdropFilter="blur(10px)"
                >
                  <HStack spacing={3} mb={2}>
                    <Flex w="34px" h="34px" align="center" justify="center" borderRadius="12px" bg="rgba(187,140,82,0.28)">
                      <Icon as={item.icon} boxSize={5} color="#f6d7aa" />
                    </Flex>
                    <Text fontWeight="800">{item.label}</Text>
                  </HStack>
                  <Text fontSize="sm" color="rgba(255,255,255,0.72)" lineHeight="1.55">
                    {item.text}
                  </Text>
                </Box>
              ))}
              <Box
                display={{ base: 'none', md: 'block' }}
                p={4}
                borderRadius="22px"
                bg="rgba(43,9,19,0.34)"
                border="1px solid rgba(255,255,255,0.12)"
              >
                <Text fontSize="xs" color="#f6d7aa" letterSpacing="0.14em" textTransform="uppercase" mb={2}>
                  Estado del acceso
                </Text>
                <Text fontWeight="800" fontSize="2xl">Seguro</Text>
                <Text fontSize="sm" color="rgba(255,255,255,0.7)">Validacion activa antes de entrar al sistema.</Text>
              </Box>
            </Grid>
          </VStack>
        </Box>

        <Flex align="center" justify="center" p={{ base: 5, md: 9, xl: 12 }}>
          <MotionBox
            as="form"
            onSubmit={handleSubmit(onSubmit)}
            w="100%"
            maxW="455px"
            bg="rgba(255,255,255,0.94)"
            borderRadius="30px"
            border="1px solid rgba(104,26,45,0.10)"
            boxShadow="0 22px 60px rgba(78, 37, 28, 0.12)"
            p={{ base: 6, md: 8 }}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.55, ease: 'easeOut' } as any}
          >
            <Box mb={7}>
              <Badge
                px={3}
                py={1.5}
                borderRadius="full"
                bg="#f7efe8"
                color="#681a2d"
                border="1px solid rgba(104,26,45,0.10)"
              >
                Acceso institucional
              </Badge>
              <Text mt={4} fontSize={{ base: '28px', md: '34px' }} fontWeight="800" color="#2b0913" letterSpacing="-1.1px">
                Iniciar sesion
              </Text>
              <Text mt={2} color="#6f5b55" fontSize="sm" lineHeight="1.7">
                Usa tus credenciales autorizadas para entrar al panel administrativo.
              </Text>
            </Box>

            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm" color="#4a332e" fontWeight="800">
                  Correo institucional
                </FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={MdEmail} color="#bb8c52" boxSize={5} />
                  </InputLeftElement>
                  <Input
                    type="email"
                    placeholder="usuario@totolac.gob.mx"
                    {...register('email', { required: true })}
                    bg="#fbf8f3"
                    border="1px solid"
                    borderColor="rgba(104,26,45,0.12)"
                    color="#2b0913"
                    h="54px"
                    borderRadius="18px"
                    _placeholder={{ color: '#a7948c' }}
                    _focus={{ bg: 'white', borderColor: '#5b182b', boxShadow: '0 0 0 3px rgba(161,38,61,0.12)' }}
                  />
                </InputGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" color="#4a332e" fontWeight="800">
                  Contrasena
                </FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={MdLock} color="#bb8c52" boxSize={5} />
                  </InputLeftElement>
                  <Input
                    type={show ? 'text' : 'password'}
                    placeholder="********"
                    {...register('password', { required: true })}
                    bg="#fbf8f3"
                    border="1px solid"
                    borderColor="rgba(104,26,45,0.12)"
                    color="#2b0913"
                    h="54px"
                    borderRadius="18px"
                    _placeholder={{ color: '#a7948c' }}
                    _focus={{ bg: 'white', borderColor: '#5b182b', boxShadow: '0 0 0 3px rgba(161,38,61,0.12)' }}
                  />
                  <InputRightElement h="54px">
                    <Icon
                      as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                      cursor="pointer"
                      onClick={() => setShow(!show)}
                      color="#7c625b"
                      _hover={{ color: '#5b182b' }}
                      transition="color 0.15s"
                      boxSize={5}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <Flex w="100%" justify="space-between" align={{ base: 'flex-start', sm: 'center' }} gap={3} direction={{ base: 'column', sm: 'row' }}>
                <Checkbox
                  colorScheme="red"
                  isChecked={mantenerSesion}
                  onChange={e => setMantenerSesion(e.target.checked)}
                  size="sm"
                >
                  <Text fontSize="sm" color="#6f5b55">Mantener sesion 30 dias</Text>
                </Checkbox>
                <HStack spacing={1.5} color="#8d786f">
                  <Icon as={MdShield} boxSize={4} />
                  <Text fontSize="xs" fontWeight="700">2FA compatible</Text>
                </HStack>
              </Flex>

              <Box
                p={3}
                bg="#fbf8f3"
                borderRadius="20px"
                border="1px solid rgba(104,26,45,0.10)"
                overflowX="auto"
              >
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITEKEY}
                  onChange={(token: any) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                  theme="light"
                />
              </Box>

              <Button
                w="100%"
                bgGradient="linear(135deg, #681a2d, #5b182b)"
                color="white"
                _hover={{ bgGradient: 'linear(135deg, #4d1425, #861f34)', transform: 'translateY(-2px)', boxShadow: '0 18px 32px rgba(104,26,45,0.24)' }}
                _active={{ transform: 'translateY(0)' }}
                transition="all 0.2s"
                type="submit"
                isLoading={isLoading}
                loadingText="Verificando..."
                isDisabled={!recaptchaToken}
                h="54px"
                borderRadius="18px"
                fontWeight="800"
                letterSpacing="0.02em"
              >
                Entrar al sistema
              </Button>
            </VStack>

            <Divider my={6} borderColor="rgba(104,26,45,0.12)" />
            <HStack justify="space-between" align="center" color="#8d786f" spacing={3}>
              <HStack spacing={2}>
                <Icon as={MdLock} boxSize={4} color="#bb8c52" />
                <Text fontSize="xs" fontWeight="700">Conexion protegida</Text>
              </HStack>
              <Text fontSize="xs">Totolac Admin</Text>
            </HStack>
          </MotionBox>
        </Flex>
      </SimpleGrid>
    </Flex>
  );
}

export default SignIn;
